import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import QRCode from 'react-qr-code';
import { useDropzone } from 'react-dropzone';
import {
  Upload, FolderUp, X, CheckCircle, File,
  Wifi, Clock, Zap, ArrowRight,
  Lock, Shield, XCircle,
  UserPlus, LogOut, Copy, Check, AlertCircle, Image as ImageIcon,
  Video, Music, FileText, Archive, Smartphone, Laptop, Monitor, Moon, Bug,
  Sun, History, MessageCircle, Send, Download, Menu
} from 'lucide-react';
import { useP2P } from './hooks/useP2P';
import type { User, IncomingRequest, TransferRecord, ChatMessage } from './hooks/useP2P';
import {
  clearDebugEvents,
  downloadDebugReport,
  getDebugEventCount,
  isDebugModeEnabled,
  logDebug,
  onDebugEvent,
} from './lib/debug';

// ─── Pure UI helpers (no simulation) ─────────────────────────────────────────
type DeviceType = 'smartphone' | 'laptop' | 'desktop';
type FloatingEmoji = { id: string; emoji: string; x: number; y: number };
type BackgroundStar = {
  id: number;
  top: string;
  left: string;
  size: string;
  opacity: number;
  twinkleSec: number;
};

function getFileIcon(type: string) {
  if (type.startsWith('image/')) return <ImageIcon className="w-6 h-6" />;
  if (type.startsWith('video/')) return <Video className="w-6 h-6" />;
  if (type.startsWith('audio/')) return <Music className="w-6 h-6" />;
  if (type.includes('pdf') || type.includes('text')) return <FileText className="w-6 h-6" />;
  if (type.includes('zip') || type.includes('tar') || type.includes('rar')) return <Archive className="w-6 h-6" />;
  return <File className="w-6 h-6" />;
}

function getDeviceIcon(device: DeviceType, className = 'w-4 h-4') {
  switch (device) {
    case 'smartphone': return <Smartphone className={className} />;
    case 'laptop':     return <Laptop     className={className} />;
    case 'desktop':    return <Monitor    className={className} />;
  }
}

function nanoidUI(len = 9): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  return Array.from(crypto.getRandomValues(new Uint8Array(len)), b => chars[b % chars.length]).join('');
}

// ─────────────────────────────────────────────────────────────────────────────
export default function App() {
  // ── All real state + handlers come from the hook (no simulations) ──────────
  const {
    me,
    peers,
    appMode, setAppMode,
    darkMode, setDarkMode,
    roomTab, setRoomTab,
    roomConfig, setRoomConfig,
    inviteUrl,
    joinInput, setJoinInput,
    copied,
    joinError, setJoinError,        // FIX #3 — error state for room join/create
    selectedPeer, setSelectedPeer,
    selectedFiles, setSelectedFiles,
    transferState,
    transferError,
    progress,
    receiverProgress,               // FIX #1 — separate progress for receiver panel
    transferRate,
    timeRemaining,
    incomingReq, setIncomingReq,
    history,
    isHistoryOpen, setIsHistoryOpen,
    messages,
    chatInput, setChatInput,
    isChatOpen, setIsChatOpen,
    handleSend,
    cancelTransfer,
    resetSelection,
    handleAcceptIncoming,
    handleRejectIncoming,
    generateRoom,
    copyRoomLink,
    startRoom,
    joinRoom,
    leaveRoom,
    handleSendMessage,
    sendEmojiToRoom,                // FIX #5 — broadcast emoji to room
  } = useP2P();

  // ── Local-only UI state (no business logic) ───────────────────────────────
  const [filePreview, setFilePreview]   = useState<string | null>(null);
  const [emojis,      setEmojis]        = useState<FloatingEmoji[]>([]);
  const [isMobile,    setIsMobile]      = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(max-width: 768px)').matches;
  });
  const [isCompactHeader, setIsCompactHeader] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(max-width: 1024px), (pointer: coarse)').matches;
  });
  const [isHeaderMenuOpen, setIsHeaderMenuOpen] = useState(false);
  const [debugEnabled] = useState<boolean>(() => isDebugModeEnabled());
  const [debugCount, setDebugCount] = useState<number>(() => getDebugEventCount());

  // ── DOM refs ──────────────────────────────────────────────────────────────
  const fileInputRef   = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const chatEndRef     = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const media = window.matchMedia('(max-width: 768px)');
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    setIsMobile(media.matches);
    media.addEventListener?.('change', onChange);
    return () => media.removeEventListener?.('change', onChange);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const widthMedia = window.matchMedia('(max-width: 1024px)');
    const pointerMedia = window.matchMedia('(pointer: coarse)');
    const update = () => setIsCompactHeader(widthMedia.matches || pointerMedia.matches);
    update();
    widthMedia.addEventListener?.('change', update);
    pointerMedia.addEventListener?.('change', update);
    return () => {
      widthMedia.removeEventListener?.('change', update);
      pointerMedia.removeEventListener?.('change', update);
    };
  }, []);

  useEffect(() => {
    if (!isCompactHeader) setIsHeaderMenuOpen(false);
  }, [isCompactHeader]);

  useEffect(() => {
    if (!debugEnabled) return;
    const off = onDebugEvent(() => setDebugCount(getDebugEventCount()));
    logDebug('ui', 'debug_mode.active', { href: window.location.href });
    return off;
  }, [debugEnabled]);

  // ── File preview (pure UI — object URL from the selected File) ─────────────
  useEffect(() => {
    if (selectedFiles.length === 0) { setFilePreview(null); return; }
    const first = selectedFiles[0];
    if (first && first.type.startsWith('image/')) {
      const url = URL.createObjectURL(first);
      setFilePreview(url);
      return () => URL.revokeObjectURL(url);
    }
    setFilePreview(null);
  }, [selectedFiles]);

  // ── Chat scroll ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isChatOpen]);

  // ── Drag-and-drop (pure UI) ────────────────────────────────────────────────
  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) setSelectedFiles(prev => [...prev, ...acceptedFiles]);
  }, [setSelectedFiles]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    noClick: true,
    noKeyboard: true,
  } as any);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0)
      setSelectedFiles(prev => [...prev, ...Array.from(e.target.files!)]);
  };

  const handleDownloadDebug = useCallback(() => {
    downloadDebugReport({
      filePrefix: 'wavedrop-debug',
      extra: {
        appMode,
        peerCount: peers.length,
        selectedPeerId: selectedPeer?.id ?? null,
        transferState,
      },
    });
  }, [appMode, peers.length, selectedPeer?.id, transferState]);

  const handleClearDebug = useCallback(() => {
    clearDebugEvents();
    setDebugCount(0);
  }, []);

  // ── Floating emoji (pure UI reaction) ─────────────────────────────────────
  const sendEmoji = (emoji: string) => {
    const id = nanoidUI();
    setEmojis(prev => [...prev, { id, emoji, x: Math.random() * 80 + 10, y: 100 }]);
    setTimeout(() => setEmojis(prev => prev.filter(e => e.id !== id)), 3000);
  };

  // ── Derived display flags ─────────────────────────────────────────────────
  const isTransferring = transferState === 'transferring' || transferState === 'awaiting_accept';
  const isReceiving    = incomingReq?.state === 'receiving';
  const showWaves      = appMode === 'local' || appMode === 'in_room';
  const waveRingCount  = isMobile ? 3 : 5;
  const waveSizeClass  = isMobile ? 'w-[760px] h-[760px]' : 'w-[1200px] h-[1200px]';
  const activeWaveClass = darkMode
    ? (isMobile
      ? 'border-indigo-400/70 bg-indigo-400/10'
      : 'border-indigo-400/90 bg-indigo-400/30 shadow-[0_0_40px_rgba(99,102,241,0.4)]')
    : (appMode === 'in_room'
      ? (isMobile
        ? 'border-indigo-400/55 bg-indigo-400/8'
        : 'border-indigo-400/60 bg-indigo-400/10 shadow-[0_0_30px_rgba(99,102,241,0.2)]')
      : (isMobile
        ? 'border-blue-400/55 bg-blue-400/8'
        : 'border-blue-400/60 bg-blue-400/10 shadow-[0_0_30px_rgba(59,130,246,0.2)]'));
  const idleWaveClass = darkMode
    ? 'border-indigo-400/50 bg-indigo-400/12'
    : (appMode === 'in_room'
      ? 'border-indigo-300/35 bg-indigo-200/10'
      : 'border-slate-300/60 bg-slate-200/20');
  const backgroundStars = useMemo<BackgroundStar[]>(
    () =>
      Array.from({ length: isMobile ? 22 : 50 }, (_, id) => ({
        id,
        top: `${Math.random() * 100}%`,
        left: `${Math.random() * 100}%`,
        size: `${Math.random() * 2 + 1}px`,
        opacity: Math.random() * 0.5 + 0.1,
        twinkleSec: Math.random() * 5 + 3,
      })),
    [isMobile]
  );

  // ─────────────────────────────────────────────────────────────────────────
  // JSX — unchanged from original design
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div {...getRootProps()} className={`h-screen w-full font-sans flex flex-col overflow-hidden relative selection:bg-blue-200 outline-none transition-colors duration-500 ${darkMode ? 'bg-[#020205] text-slate-200' : 'bg-[#ffffff] text-slate-800'}`}>
      <input {...getInputProps()} />

      {/* Global Drag Overlay */}
      <AnimatePresence>
        {isDragActive && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="absolute inset-0 z-[100] bg-blue-900/40 backdrop-blur-sm flex items-center justify-center border-8 border-blue-500/50 m-4 rounded-[3rem]"
          >
            <div className="bg-white/90 px-8 py-6 rounded-3xl shadow-2xl flex flex-col items-center gap-4">
              <div className="w-20 h-20 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center animate-bounce">
                <Upload className="w-10 h-10" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">Drop file to share</h2>
              <p className="text-slate-500 font-medium">Release to select this file</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Soft Animated Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none transition-opacity duration-500">
        <div className={`absolute top-[-20%] left-[-10%] w-[60%] h-[60%] rounded-full blur-[150px] transition-colors duration-700 ${darkMode ? 'bg-indigo-900/40 mix-blend-screen' : 'bg-blue-300/30 mix-blend-multiply'}`}></div>
        <div className={`absolute bottom-[-20%] right-[-10%] w-[60%] h-[60%] rounded-full blur-[150px] transition-colors duration-700 ${darkMode ? 'bg-blue-900/40 mix-blend-screen' : 'bg-indigo-300/30 mix-blend-multiply'}`}></div>
        <AnimatePresence>
          {darkMode && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 1 }} className="absolute inset-0">
              {backgroundStars.map((star) => (
                <div
                  key={star.id}
                  className="absolute rounded-full bg-white"
                  style={{
                    top: star.top,
                    left: star.left,
                    width: star.size,
                    height: star.size,
                    opacity: star.opacity,
                    animation: `twinkle ${star.twinkleSec}s infinite alternate`,
                  }}
                />
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Header */}
      <header className="absolute top-3 md:top-6 left-1/2 -translate-x-1/2 z-40 w-[calc(100%-1rem)] md:w-[96%] max-w-6xl rounded-3xl md:rounded-full border shadow-lg backdrop-blur-2xl transition-colors duration-500 pointer-events-none px-3 py-2.5 md:px-6 md:py-3" style={{ backgroundColor: darkMode ? 'rgba(15, 15, 20, 0.6)' : 'rgba(255, 255, 255, 0.7)', borderColor: darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)' }}>
        {isCompactHeader ? (
          <>
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 pointer-events-auto min-w-0">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center shadow-inner border transition-colors duration-500 shrink-0 ${darkMode ? 'bg-gradient-to-br from-slate-800 to-slate-900 border-slate-700' : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/60'}`}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-blue-500 drop-shadow-sm">
                    <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h1 className={`text-base font-bold tracking-tight leading-none truncate ${darkMode ? 'text-white' : 'text-slate-900'}`}>WaveDrop</h1>
              </div>

              <div className="pointer-events-auto flex items-center gap-2 shrink-0">
                <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${darkMode ? 'bg-black/40 border-white/5 text-slate-300' : 'bg-slate-100/60 border-black/5 text-slate-600'}`}>
                  {appMode === 'local' ? (
                    <><Wifi className="w-3 h-3 text-emerald-500" /><span>Local</span></>
                  ) : appMode === 'in_room' ? (
                    <><Shield className="w-3 h-3 text-indigo-500" /><span>Room</span></>
                  ) : (
                    <><Lock className="w-3 h-3 text-slate-400" /><span>Setup</span></>
                  )}
                </div>
                <button
                  onClick={() => setDarkMode(!darkMode)}
                  className={`p-2 rounded-full shadow-sm border transition-all duration-300 ${darkMode ? 'bg-slate-800/60 border-slate-700/60 text-amber-400' : 'bg-white/80 border-slate-200/80 text-indigo-500'}`}
                >
                  {darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                </button>
                <button
                  onClick={() => setIsHeaderMenuOpen((prev) => !prev)}
                  className={`p-2 rounded-full shadow-sm border transition-all duration-300 ${darkMode ? 'bg-slate-800/60 border-slate-700/60 text-slate-200' : 'bg-white/80 border-slate-200/80 text-slate-700'}`}
                >
                  {isHeaderMenuOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <AnimatePresence>
              {isHeaderMenuOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.18 }}
                  className={`pointer-events-auto mt-2 rounded-2xl border p-2 grid grid-cols-1 gap-2 ${darkMode ? 'bg-[#0b0b12]/90 border-slate-800' : 'bg-white/90 border-slate-200'}`}
                >
                  <button
                    onClick={() => { setAppMode('local'); setIsHeaderMenuOpen(false); }}
                    className={`px-3 py-2 rounded-xl text-xs font-bold border transition-colors ${appMode === 'local' ? (darkMode ? 'bg-slate-800 border-slate-700 text-blue-300' : 'bg-blue-50 border-blue-200 text-blue-700') : (darkMode ? 'bg-slate-900/60 border-slate-800 text-slate-300' : 'bg-white border-slate-200 text-slate-600')}`}
                  >
                    Local Network
                  </button>
                  <button
                    onClick={() => {
                      if (appMode !== 'room_setup') generateRoom();
                      setAppMode('room_setup');
                      setIsHeaderMenuOpen(false);
                    }}
                    className={`px-3 py-2 rounded-xl text-xs font-bold border transition-colors ${appMode !== 'local' ? (darkMode ? 'bg-slate-800 border-slate-700 text-indigo-300' : 'bg-indigo-50 border-indigo-200 text-indigo-700') : (darkMode ? 'bg-slate-900/60 border-slate-800 text-slate-300' : 'bg-white border-slate-200 text-slate-600')}`}
                  >
                    Private Room
                  </button>

                  <button
                    onClick={() => { setIsHistoryOpen(true); setIsHeaderMenuOpen(false); }}
                    className={`flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-xs font-bold border transition-colors ${darkMode ? 'bg-slate-900/60 border-slate-800 text-slate-200' : 'bg-white border-slate-200 text-slate-700'}`}
                  >
                    <History className="w-3.5 h-3.5" />
                    <span>History</span>
                  </button>

                  {debugEnabled && (
                    <>
                      <button
                        onClick={handleDownloadDebug}
                        className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold border transition-colors ${darkMode ? 'bg-rose-900/30 border-rose-700/40 text-rose-200' : 'bg-rose-50 border-rose-200 text-rose-700'}`}
                      >
                        <Bug className="w-3.5 h-3.5" />
                        <span>{debugCount}</span>
                        <Download className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={handleClearDebug}
                        className={`px-3 py-2 rounded-xl text-xs font-bold border transition-colors ${darkMode ? 'bg-slate-900/60 border-slate-800 text-slate-300' : 'bg-white border-slate-200 text-slate-600'}`}
                      >
                        Clear Debug
                      </button>
                    </>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </>
        ) : (
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3 pointer-events-auto">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center shadow-inner border transition-colors duration-500 ${darkMode ? 'bg-gradient-to-br from-slate-800 to-slate-900 border-slate-700' : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/60'}`}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-blue-500 drop-shadow-sm">
                  <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div>
                <h1 className={`text-lg font-bold tracking-tight leading-none transition-colors duration-500 ${darkMode ? 'text-white' : 'text-slate-900'}`}>WaveDrop</h1>
              </div>
            </div>

            <div className={`pointer-events-auto flex items-center rounded-full p-1 shadow-inner border transition-colors duration-500 ${darkMode ? 'bg-black/40 border-white/5' : 'bg-slate-100/50 border-black/5'}`}>
              <button
                onClick={() => setAppMode('local')}
                className={`px-5 py-1.5 rounded-full text-sm font-semibold transition-all duration-300 ${appMode === 'local' ? (darkMode ? 'bg-slate-800 shadow-md text-blue-400' : 'bg-white shadow-md text-blue-600') : (darkMode ? 'text-slate-400 hover:text-slate-200' : 'text-slate-500 hover:text-slate-800')}`}
              >
                Local Network
              </button>
              <button
                onClick={() => {
                  if (appMode !== 'room_setup') generateRoom();
                  setAppMode('room_setup');
                }}
                className={`px-5 py-1.5 rounded-full text-sm font-semibold transition-all duration-300 ${appMode !== 'local' ? (darkMode ? 'bg-slate-800 shadow-md text-indigo-400' : 'bg-white shadow-md text-indigo-600') : (darkMode ? 'text-slate-400 hover:text-slate-200' : 'text-slate-500 hover:text-slate-800')}`}
              >
                Private Room
              </button>
            </div>

            <div className="pointer-events-auto flex items-center gap-3">
              <div className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider shadow-inner border transition-colors duration-500 ${darkMode ? 'bg-black/40 border-white/5 text-slate-300' : 'bg-slate-100/50 border-black/5 text-slate-600'}`}>
                {appMode === 'local' ? (
                  <><Wifi className="w-3.5 h-3.5 text-emerald-500" /><span>Local</span></>
                ) : appMode === 'in_room' ? (
                  <><Shield className="w-3.5 h-3.5 text-indigo-500" /><span>Room: {roomConfig.id || joinInput.id}</span></>
                ) : (
                  <><Lock className="w-3.5 h-3.5 text-slate-400" /><span>Setup</span></>
                )}
              </div>
              <div className="w-px h-6 bg-slate-500/20 mx-1"></div>
              <button
                onClick={() => setIsHistoryOpen(true)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold shadow-sm border transition-all duration-300 hover:scale-105 ${darkMode ? 'bg-slate-800/50 border-slate-700/50 text-slate-200 hover:bg-slate-700/80 hover:border-slate-600' : 'bg-white/80 backdrop-blur-md border-slate-200/80 text-slate-700 hover:bg-white hover:shadow-md'}`}
              >
                <History className="w-4 h-4" />
                <span>History</span>
              </button>
              {debugEnabled && (
                <>
                  <button
                    onClick={handleDownloadDebug}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-bold shadow-sm border transition-all duration-300 hover:scale-105 ${darkMode ? 'bg-rose-900/40 border-rose-500/40 text-rose-200 hover:bg-rose-900/60' : 'bg-rose-50 border-rose-200 text-rose-700 hover:bg-rose-100'}`}
                  >
                    <Bug className="w-4 h-4" />
                    <span>Debug {debugCount}</span>
                    <Download className="w-4 h-4" />
                  </button>
                  <button
                    onClick={handleClearDebug}
                    className={`px-3 py-2.5 rounded-full text-xs font-bold shadow-sm border transition-all duration-300 hover:scale-105 ${darkMode ? 'bg-slate-800/50 border-slate-700/50 text-slate-300 hover:bg-slate-700/80' : 'bg-white/80 border-slate-200 text-slate-600 hover:bg-white'}`}
                  >
                    Clear
                  </button>
                </>
              )}
              <button
                onClick={() => setDarkMode(!darkMode)}
                className={`p-2.5 rounded-full shadow-sm border transition-all duration-300 hover:scale-105 ${darkMode ? 'bg-slate-800/50 border-slate-700/50 text-amber-400 hover:bg-slate-700/80 hover:border-slate-600' : 'bg-white/80 backdrop-blur-md border-slate-200/80 text-indigo-500 hover:bg-white hover:shadow-md'}`}
              >
                {darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </button>
            </div>
          </div>
        )}
      </header>

      {/* Main Area */}
      <main className="flex-1 relative flex items-center justify-center">

        {/* Waves — two permanently-running CSS groups, cross-faded by opacity.
            NEVER changes animationDuration at runtime → zero flicker.
            Idle rings always run at ~6 s; active rings always run at ~2 s.
            Switching between them is a 400 ms opacity fade, not an animation restart. */}
        {showWaves && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">

            {/* ── Idle wave group (slow, always running) ── */}
            <div
              className="absolute inset-0 flex items-center justify-center"
              style={{
                opacity:    isTransferring || isReceiving ? 0 : 1,
                transition: 'opacity 0.4s ease',
              }}
            >
              {Array.from({ length: waveRingCount }, (_, i) => (
                <div
                  key={`idle-${i}`}
                  className={`absolute ${waveSizeClass} rounded-full border will-change-transform wave-idle ${idleWaveClass}`}
                  style={{
                    animationDuration: `${isMobile ? 7 : 6}s`,
                    animationDelay:    `${i * ((isMobile ? 7 : 6) / waveRingCount)}s`,
                  }}
                />
              ))}
            </div>

            {/* ── Active wave group (fast, always running) ── */}
            <div
              className="absolute inset-0 flex items-center justify-center"
              style={{
                opacity:    isTransferring || isReceiving ? 1 : 0,
                transition: 'opacity 0.4s ease',
              }}
            >
              {Array.from({ length: waveRingCount }, (_, i) => (
                <div
                  key={`active-${i}`}
                  className={`absolute ${waveSizeClass} rounded-full border will-change-transform wave-active ${activeWaveClass}`}
                  style={{
                    animationDuration: `${isMobile ? 2.8 : 2}s`,
                    animationDelay:    `${i * ((isMobile ? 2.8 : 2) / waveRingCount)}s`,
                  }}
                />
              ))}
            </div>

          </div>
        )}

        {/* Center: "You" */}
        {showWaves && (
          <div className="relative z-20 flex flex-col items-center">
            <motion.div
              className={`w-32 h-32 rounded-full flex items-center justify-center relative z-10 ${isTransferring || isReceiving ? (appMode === 'in_room' ? 'shadow-[0_0_50px_rgba(99,102,241,0.5)]' : 'shadow-[0_0_50px_rgba(59,130,246,0.5)]') : (darkMode ? 'shadow-[0_0_40px_rgba(0,0,0,0.8)]' : 'shadow-[0_0_40px_rgba(0,0,0,0.1)]')}`}
              animate={isTransferring || isReceiving ? { scale: [1, 1.05, 1] } : { scale: 1 }}
              transition={{ repeat: Infinity, duration: 2 }}
            >
              <div className={`absolute inset-0 rounded-full border-2 border-dashed animate-[spin_10s_linear_infinite] ${darkMode ? 'border-indigo-500/50' : 'border-blue-500/50'}`}></div>
              <img src={me.avatar} alt="My Avatar" className={`w-[116px] h-[116px] rounded-full object-cover border-4 ${darkMode ? 'border-[#020205]' : 'border-white'}`} />
            </motion.div>
            <div className={`absolute -bottom-6 px-5 py-2 rounded-full shadow-lg border backdrop-blur-xl text-center z-20 flex items-center gap-2 ${darkMode ? 'bg-slate-900/80 border-slate-700' : 'bg-white/90 border-slate-200/50'}`}>
              <div className={darkMode ? 'text-slate-400' : 'text-slate-500'}>{getDeviceIcon(me.device)}</div>
              <div>
                <p className={`text-sm font-bold leading-tight ${darkMode ? 'text-white' : 'text-slate-800'}`}>{me.name}</p>
                <p className={`text-[10px] font-bold uppercase tracking-wider leading-tight ${darkMode ? 'text-indigo-400' : 'text-blue-500'}`}>You</p>
              </div>
            </div>
          </div>
        )}

        {/* Peers */}
        {showWaves && (
          <AnimatePresence>
            {peers.map((peer, index) => {
              const angle  = (index * (360 / Math.max(peers.length, 1))) - 45;
              const radius = 240;
              const x = Math.cos(angle * (Math.PI / 180)) * radius;
              const y = Math.sin(angle * (Math.PI / 180)) * radius;
              const isSelected          = selectedPeer?.id === peer.id;
              const isReceivingFromThis = incomingReq?.peer.id === peer.id && isReceiving;
              const isSendingToThis     = isSelected && isTransferring;
              const hasActiveBeam       = isSendingToThis || isReceivingFromThis;
              const themeColor          = appMode === 'in_room' ? 'indigo' : 'blue';

              return (
                <motion.div
                  key={peer.id}
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1, x, y }}
                  exit={{ scale: 0, opacity: 0 }}
                  transition={{ type: 'spring', stiffness: 200, damping: 20 }}
                  className="absolute z-20 flex flex-col items-center cursor-pointer group"
                  onClick={() => { if (transferState === 'idle' && !incomingReq) setSelectedPeer(peer); }}
                >
                  {hasActiveBeam && (
                    <motion.div
                      className={`absolute top-1/2 left-1/2 h-[2px] ${themeColor === 'indigo' ? 'bg-indigo-500' : 'bg-blue-500'} origin-left z-0`}
                      style={{ width: radius, rotate: isReceivingFromThis ? angle : angle + 180, translateY: '-50%' }}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: [0.4, 1, 0.4] }}
                      transition={{ repeat: Infinity, duration: 1.5 }}
                    >
                      <motion.div className="w-8 h-full bg-white blur-[2px]" animate={{ x: [0, radius] }} transition={{ repeat: Infinity, duration: 0.8, ease: 'linear' }} />
                    </motion.div>
                  )}
                  <motion.div
                    className={`w-20 h-20 rounded-full flex items-center justify-center relative transition-all duration-300 z-10 ${isSelected || isReceivingFromThis ? hasActiveBeam ? (themeColor === 'indigo' ? 'shadow-[0_0_30px_rgba(99,102,241,0.6)]' : 'shadow-[0_0_30px_rgba(59,130,246,0.6)]') : (themeColor === 'indigo' ? 'shadow-[0_0_20px_rgba(99,102,241,0.4)]' : 'shadow-[0_0_20px_rgba(59,130,246,0.4)]') : (darkMode ? 'shadow-[0_0_20px_rgba(0,0,0,0.5)]' : 'shadow-[0_0_20px_rgba(0,0,0,0.1)]')} ${isDragActive ? (themeColor === 'indigo' ? 'ring-4 ring-indigo-400 ring-offset-2 animate-pulse' : 'ring-4 ring-blue-400 ring-offset-2 animate-pulse') : ''}`}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {(isSelected || isReceivingFromThis) && (
                      <div className={`absolute inset-0 rounded-full border-2 border-dashed animate-[spin_4s_linear_infinite] ${themeColor === 'indigo' ? 'border-indigo-400' : 'border-blue-400'}`}></div>
                    )}
                    <img src={peer.avatar} alt={peer.name} className={`w-[72px] h-[72px] rounded-full object-cover border-2 ${darkMode ? 'border-[#020205]' : 'border-white'}`} />
                  </motion.div>
                  <div className={`mt-3 px-4 py-1.5 rounded-full shadow-lg border backdrop-blur-xl transition-colors duration-300 relative z-10 flex items-center gap-1.5 ${isSelected || isReceivingFromThis ? (themeColor === 'indigo' ? 'bg-indigo-600 text-white border-indigo-500' : 'bg-blue-600 text-white border-blue-500') : (darkMode ? 'bg-slate-900/80 border-slate-700 text-slate-200 group-hover:border-indigo-500/50' : 'bg-white/90 border-slate-200/50 text-slate-700 group-hover:border-blue-300')}`}>
                    <div className={isSelected || isReceivingFromThis ? 'text-white/80' : 'text-slate-400'}>{getDeviceIcon(peer.device, 'w-3 h-3')}</div>
                    <p className="text-xs font-bold whitespace-nowrap">{peer.name}</p>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        )}

        {/* Scanning Indicator */}
        {showWaves && peers.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className={`absolute bottom-12 flex items-center gap-3 px-6 py-3 rounded-full shadow-lg border backdrop-blur-xl transition-colors duration-500 ${darkMode ? 'bg-slate-900/60 border-slate-700/50 text-slate-300' : 'bg-white/60 border-white/50 text-slate-600'}`}
          >
            <div className={`w-2 h-2 rounded-full animate-ping ${appMode === 'in_room' ? (darkMode ? 'bg-indigo-400' : 'bg-indigo-500') : (darkMode ? 'bg-blue-400' : 'bg-blue-500')}`}></div>
            <span className="font-medium text-sm">
              {appMode === 'in_room' ? 'Waiting for others to join...' : 'Scanning for nearby devices...'}
            </span>
          </motion.div>
        )}

        {/* Room Setup Modal */}
        <AnimatePresence>
          {appMode === 'room_setup' && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className={`backdrop-blur-3xl rounded-[2rem] shadow-2xl border overflow-hidden flex flex-col w-full max-w-md z-50 transition-colors duration-500 ${darkMode ? 'bg-[#0a0a10]/90 border-slate-800/50 shadow-black/50' : 'bg-white/90 border-slate-200/50 shadow-slate-900/10'}`}
            >
              <div className={`flex p-2 border-b transition-colors duration-500 ${darkMode ? 'bg-slate-900/50 border-slate-800/50' : 'bg-slate-100/50 border-slate-200/50'}`}>
                <button onClick={() => setRoomTab('create')} className={`flex-1 py-3 text-sm font-bold rounded-xl transition-all ${roomTab === 'create' ? (darkMode ? 'bg-slate-800 shadow-sm text-indigo-400' : 'bg-white shadow-sm text-indigo-600') : (darkMode ? 'text-slate-500 hover:text-slate-300' : 'text-slate-500 hover:text-slate-700')}`}>Create Room</button>
                <button onClick={() => setRoomTab('join')}   className={`flex-1 py-3 text-sm font-bold rounded-xl transition-all ${roomTab === 'join'   ? (darkMode ? 'bg-slate-800 shadow-sm text-indigo-400' : 'bg-white shadow-sm text-indigo-600') : (darkMode ? 'text-slate-500 hover:text-slate-300' : 'text-slate-500 hover:text-slate-700')}`}>Join Room</button>
              </div>

              <div className="p-8">
                <AnimatePresence mode="wait">
                  {roomTab === 'create' ? (
                    <motion.div key="create" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} className="flex flex-col items-center">
                      <div className={`p-4 rounded-2xl shadow-sm border mb-6 transition-colors duration-500 ${darkMode ? 'bg-slate-200 border-slate-300' : 'bg-white border-slate-200'}`}>
                        <QRCode
                          value={inviteUrl || `${window.location.origin}/room/${roomConfig.id}${roomConfig.usePassword ? `?pwd=${roomConfig.password}` : ''}`}
                          size={160}
                          className={darkMode ? 'text-indigo-900' : 'text-indigo-600'}
                        />
                      </div>
                      <div className="w-full space-y-4">
                        <div className={`flex items-center justify-between p-4 rounded-2xl border transition-colors duration-500 ${darkMode ? 'bg-slate-900/50 border-slate-800/50' : 'bg-slate-50 border-slate-200/60'}`}>
                          <div>
                            <p className={`text-xs font-bold uppercase tracking-wider ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Room Code</p>
                            <p className={`text-2xl font-black tracking-widest ${darkMode ? 'text-slate-200' : 'text-slate-800'}`}>{roomConfig.id || '------'}</p>
                          </div>
                          <button onClick={copyRoomLink} className={`p-3 rounded-xl shadow-sm border transition-colors ${darkMode ? 'bg-slate-800 border-slate-700 text-indigo-400 hover:bg-slate-700' : 'bg-white border-slate-200 text-indigo-600 hover:bg-indigo-50'}`}>
                            {copied ? <Check className="w-5 h-5 text-emerald-500" /> : <Copy className="w-5 h-5" />}
                          </button>
                        </div>
                        <div className="flex items-center justify-between px-2">
                          <label className={`flex items-center gap-2 text-sm font-bold cursor-pointer ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                            <input
                              type="checkbox"
                              checked={roomConfig.usePassword}
                              onChange={(e) => setRoomConfig(prev => ({ ...prev, usePassword: e.target.checked }))}
                              className={`w-4 h-4 rounded focus:ring-indigo-500 ${darkMode ? 'bg-slate-800 border-slate-700 text-indigo-500' : 'border-slate-300 text-indigo-600'}`}
                            />
                            Require Password
                          </label>
                          {roomConfig.usePassword && (
                            <span className={`text-sm font-mono font-bold px-3 py-1 rounded-lg ${darkMode ? 'text-indigo-400 bg-indigo-500/20' : 'text-indigo-600 bg-indigo-50'}`}>{roomConfig.password}</span>
                          )}
                        </div>
                        <button onClick={startRoom} className={`w-full py-4 mt-4 rounded-2xl font-bold text-white shadow-lg transition-all flex items-center justify-center gap-2 ${darkMode ? 'bg-indigo-600 hover:bg-indigo-500 shadow-indigo-500/20' : 'bg-indigo-600 hover:bg-indigo-700 shadow-indigo-600/20'}`}>
                          Start Room <ArrowRight className="w-5 h-5" />
                        </button>
                        {joinError && (
                          <div className={`mt-3 px-4 py-3 rounded-2xl text-sm font-semibold flex items-center gap-2 ${darkMode ? 'bg-rose-500/15 text-rose-400 border border-rose-500/20' : 'bg-rose-50 text-rose-700 border border-rose-200'}`}>
                            <AlertCircle className="w-4 h-4 flex-none" />
                            <span>{joinError}</span>
                            <button onClick={() => setJoinError(null)} className="ml-auto opacity-60 hover:opacity-100"><X className="w-4 h-4" /></button>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  ) : (
                    <motion.div key="join" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="flex flex-col">
                      <div className="space-y-5">
                        <div>
                          <label className={`block text-xs font-bold uppercase tracking-wider mb-2 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Room Code</label>
                          <div className="relative">
                            <UserPlus className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`} />
                            <input
                              type="text" placeholder="e.g. 123456"
                              value={joinInput.id}
                              onChange={(e) => setJoinInput(prev => ({ ...prev, id: e.target.value }))}
                              className={`w-full pl-12 pr-4 py-4 border rounded-2xl font-bold focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all ${darkMode ? 'bg-slate-900/50 border-slate-800 text-slate-200 placeholder-slate-600' : 'bg-slate-50 border-slate-200 text-slate-800'}`}
                            />
                          </div>
                        </div>
                        <div>
                          <label className={`block text-xs font-bold uppercase tracking-wider mb-2 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Password (Optional)</label>
                          <div className="relative">
                            <Lock className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`} />
                            <input
                              type="password" placeholder="••••"
                              value={joinInput.password}
                              onChange={(e) => setJoinInput(prev => ({ ...prev, password: e.target.value }))}
                              className={`w-full pl-12 pr-4 py-4 border rounded-2xl font-bold focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all ${darkMode ? 'bg-slate-900/50 border-slate-800 text-slate-200 placeholder-slate-600' : 'bg-slate-50 border-slate-200 text-slate-800'}`}
                            />
                          </div>
                        </div>
                        <button
                          onClick={joinRoom}
                          disabled={joinInput.id.length < 6}
                          className={`w-full py-4 mt-2 rounded-2xl font-bold text-white disabled:opacity-50 disabled:cursor-not-allowed shadow-lg transition-all flex items-center justify-center gap-2 ${darkMode ? 'bg-indigo-600 hover:bg-indigo-500 shadow-indigo-500/20' : 'bg-indigo-600 hover:bg-indigo-700 shadow-indigo-600/20'}`}
                        >
                          Join Room <ArrowRight className="w-5 h-5" />
                        </button>
                        {joinError && (
                          <div className={`mt-3 px-4 py-3 rounded-2xl text-sm font-semibold flex items-center gap-2 ${darkMode ? 'bg-rose-500/15 text-rose-400 border border-rose-500/20' : 'bg-rose-50 text-rose-700 border border-rose-200'}`}>
                            <AlertCircle className="w-4 h-4 flex-none" />
                            <span>{joinError}</span>
                            <button onClick={() => setJoinError(null)} className="ml-auto opacity-60 hover:opacity-100"><X className="w-4 h-4" /></button>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Leave Room Button */}
      {appMode === 'in_room' && (
        <div className="absolute top-24 right-8 z-40">
          <button onClick={leaveRoom} className={`flex items-center gap-2 px-5 py-2.5 backdrop-blur-xl rounded-full text-sm font-bold border shadow-lg transition-all duration-300 hover:scale-105 ${darkMode ? 'bg-rose-500/10 text-rose-400 border-rose-500/20 hover:bg-rose-500/20' : 'bg-white/90 text-rose-600 border-rose-200 hover:bg-rose-50'}`}>
            <LogOut className="w-4 h-4" /> Leave Room
          </button>
        </div>
      )}

      {/* Floating Chat Button */}
      <div className="absolute bottom-8 right-8 z-[60]">
        <button
          onClick={() => setIsChatOpen(!isChatOpen)}
          className={`relative flex items-center justify-center w-16 h-16 rounded-full shadow-2xl transition-all duration-300 hover:scale-105 border-2 ${isChatOpen ? (darkMode ? 'bg-slate-800 text-slate-400 border-slate-700' : 'bg-slate-100 text-slate-500 border-slate-200') : (darkMode ? 'bg-indigo-600 text-white border-indigo-500 shadow-indigo-500/30' : 'bg-blue-600 text-white border-blue-500 shadow-blue-500/30')}`}
        >
          {isChatOpen ? <X className="w-7 h-7" /> : <MessageCircle className="w-7 h-7" />}
          {messages.length > 0 && !isChatOpen && (
            <span className="absolute top-0 right-0 w-4 h-4 bg-rose-500 border-2 border-[#020205] rounded-full animate-pulse"></span>
          )}
        </button>
      </div>

      {/* Chat Panel */}
      <AnimatePresence>
        {isChatOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className={`absolute bottom-28 right-8 w-80 h-[450px] max-h-[60vh] rounded-3xl shadow-2xl flex flex-col overflow-hidden border z-[50] transition-colors duration-500 ${darkMode ? 'bg-[#0a0a10]/95 border-slate-800 backdrop-blur-3xl' : 'bg-white/95 border-slate-200 backdrop-blur-3xl'}`}
          >
            <div className={`p-4 border-b flex items-center justify-between transition-colors duration-500 ${darkMode ? 'border-slate-800/50 bg-slate-900/50' : 'border-slate-100 bg-slate-50/50'}`}>
              <h3 className={`font-bold flex items-center gap-2 transition-colors duration-500 ${darkMode ? 'text-slate-200' : 'text-slate-800'}`}>
                <div className={`p-1.5 rounded-lg ${darkMode ? 'bg-indigo-500/20 text-indigo-400' : 'bg-blue-100 text-blue-600'}`}>
                  <MessageCircle className="w-4 h-4" />
                </div>
                Room Chat
              </h3>
              <button onClick={() => setIsChatOpen(false)} className={`p-1.5 rounded-full transition-all duration-300 ${darkMode ? 'hover:bg-slate-800 text-slate-400 hover:text-white' : 'hover:bg-slate-200 text-slate-500 hover:text-slate-900'}`}>
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-slate-400 text-sm text-center">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${darkMode ? 'bg-slate-800/50' : 'bg-slate-100'}`}>
                    <MessageCircle className="w-8 h-8 opacity-40" />
                  </div>
                  <p className={`font-bold text-base mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>No messages yet</p>
                  <p className="opacity-60">Say hi to the room!</p>
                </div>
              ) : (
                messages.map(msg => {
                  const isMe = msg.sender.id === me.id;
                  return (
                    <div key={msg.id} className={`flex flex-col ${isMe ? 'items-end' : 'items-start'}`}>
                      <div className={`flex items-end gap-2 max-w-[85%] ${isMe ? 'flex-row-reverse' : 'flex-row'}`}>
                        <img src={msg.sender.avatar} alt={msg.sender.name} className={`w-6 h-6 rounded-full flex-none object-cover border ${darkMode ? 'border-slate-700' : 'border-slate-200'}`} />
                        <div className={`px-4 py-2.5 rounded-2xl text-sm shadow-sm ${isMe ? (darkMode ? 'bg-indigo-600 text-white rounded-br-sm shadow-indigo-500/20' : 'bg-blue-600 text-white rounded-br-sm shadow-blue-500/20') : (darkMode ? 'bg-slate-800/80 text-slate-200 rounded-bl-sm border border-slate-700/50' : 'bg-slate-100 text-slate-800 rounded-bl-sm border border-slate-200/50')}`}>
                          {msg.text}
                        </div>
                      </div>
                      <span className={`text-[10px] mt-1.5 px-8 font-medium transition-colors duration-500 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                        {msg.sender.name} • {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  );
                })
              )}
              <div ref={chatEndRef} />
            </div>

            <div className={`p-3 border-t transition-colors duration-500 ${darkMode ? 'border-slate-800 bg-slate-900/80' : 'border-slate-100 bg-white/80'}`}>
              <div className="flex gap-2 mb-2">
                {['👍', '❤️', '😂', '🎉'].map(emoji => (
                  <button key={emoji} onClick={() => {
                    sendEmoji(emoji);          // local floating animation (always)
                    sendEmojiToRoom(emoji);    // FIX #5 — broadcast to room over WS
                  }} className={`flex-1 py-1.5 rounded-xl text-lg hover:scale-110 transition-all duration-300 ${darkMode ? 'bg-slate-800/50 hover:bg-slate-700' : 'bg-slate-50 hover:bg-slate-100 shadow-sm'}`}>
                    {emoji}
                  </button>
                ))}
              </div>
              <form onSubmit={handleSendMessage} className="flex gap-2 relative">
                <input
                  type="text"
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  placeholder="Type a message..."
                  className={`flex-1 px-4 py-2.5 rounded-2xl text-sm focus:outline-none focus:ring-2 transition-all duration-300 border ${darkMode ? 'bg-slate-800/50 text-white placeholder-slate-500 border-slate-700 focus:ring-indigo-500/50 focus:border-indigo-500' : 'bg-slate-50 text-slate-900 border-slate-200 focus:ring-blue-500/20 focus:border-blue-500 shadow-inner'}`}
                />
                <button
                  type="submit"
                  disabled={!chatInput.trim()}
                  className={`p-2.5 rounded-2xl flex items-center justify-center transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed ${chatInput.trim() ? (darkMode ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/30 hover:bg-indigo-500' : 'bg-blue-600 text-white shadow-lg shadow-blue-500/30 hover:bg-blue-500') : (darkMode ? 'bg-slate-800 text-slate-500' : 'bg-slate-200 text-slate-400')}`}
                >
                  <Send className="w-4 h-4" />
                </button>
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Emojis */}
      <div className="absolute inset-0 pointer-events-none z-50 overflow-hidden">
        <AnimatePresence>
          {emojis.map(emoji => (
            <motion.div
              key={emoji.id}
              initial={{ opacity: 0, y: '100vh', x: `${emoji.x}vw`, scale: 0.5 }}
              animate={{ opacity: [0, 1, 1, 0], y: '-10vh', scale: [0.5, 1.5, 1.5, 1] }}
              exit={{ opacity: 0 }}
              transition={{ duration: 3, ease: 'easeOut' }}
              className="absolute text-4xl"
            >
              {emoji.emoji}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Incoming Request Modal */}
      <AnimatePresence mode="wait">
        {incomingReq && incomingReq.state === 'pending' && (
          <motion.div
            key="incoming-pending"
            initial={{ opacity: 0, scale: 0.9, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-sm"
          >
            <div className={`backdrop-blur-3xl rounded-3xl shadow-2xl border p-6 flex flex-col items-center text-center transition-colors duration-500 ${darkMode ? 'bg-[#0a0a10]/90 border-slate-800/50' : 'bg-white/90 border-slate-200/50'}`}>
              <div className="relative mb-4">
                <img src={incomingReq.peer.avatar} alt="Peer" className={`w-20 h-20 rounded-full border-4 shadow-md ${darkMode ? 'border-slate-800' : 'border-white'}`} />
                <div className={`absolute -bottom-2 -right-2 text-white p-1.5 rounded-full border-2 ${darkMode ? 'bg-indigo-500 border-slate-800' : 'bg-blue-500 border-white'}`}>
                  {getFileIcon(incomingReq.fileType)}
                </div>
              </div>
              <h3 className={`text-xl font-bold mb-1 ${darkMode ? 'text-white' : 'text-slate-900'}`}>{incomingReq.peer.name}</h3>
              <p className={`text-sm mb-6 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>wants to send you a file</p>
              <div className={`w-full rounded-2xl p-4 border mb-6 flex items-center gap-3 text-left ${darkMode ? 'bg-slate-900/50 border-slate-800/50' : 'bg-slate-50 border-slate-200/60'}`}>
                <div className={`p-2 rounded-xl shadow-sm ${darkMode ? 'bg-slate-800' : 'bg-white'}`}>{getFileIcon(incomingReq.fileType)}</div>
                <div className="flex-1 min-w-0">
                  <p className={`font-bold text-sm truncate ${darkMode ? 'text-slate-200' : 'text-slate-800'}`}>{incomingReq.fileName}</p>
                  <p className={`text-xs font-medium ${darkMode ? 'text-slate-500' : 'text-slate-500'}`}>{incomingReq.fileSize} MB</p>
                </div>
              </div>
              <div className="flex gap-3 w-full">
                <button onClick={handleRejectIncoming} className={`flex-1 py-3 rounded-xl font-bold transition-colors ${darkMode ? 'text-slate-300 bg-slate-800 hover:bg-slate-700' : 'text-slate-600 bg-slate-100 hover:bg-slate-200'}`}>Decline</button>
                <button onClick={handleAcceptIncoming} className={`flex-1 py-3 rounded-xl font-bold text-white shadow-lg transition-colors ${darkMode ? 'bg-indigo-600 hover:bg-indigo-700 shadow-indigo-600/20' : 'bg-blue-600 hover:bg-blue-700 shadow-blue-600/20'}`}>Accept</button>
              </div>
            </div>
          </motion.div>
        )}

        {incomingReq && (incomingReq.state === 'receiving' || incomingReq.state === 'completed' || incomingReq.state === 'rejected') && (
          <motion.div
            initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 50 }}
            className="absolute bottom-8 left-1/2 -translate-x-1/2 z-50 w-full max-w-md"
          >
            <div className={`backdrop-blur-3xl rounded-3xl shadow-2xl border p-6 transition-colors duration-500 ${darkMode ? 'bg-[#0a0a10]/90 border-slate-800/50' : 'bg-white/90 border-slate-200/50'}`}>
              {incomingReq.state === 'rejected' ? (
                <div className={`flex items-center gap-4 ${darkMode ? 'text-rose-400' : 'text-rose-600'}`}>
                  <XCircle className="w-8 h-8" />
                  <div><p className="font-bold">Transfer Declined</p><p className="text-sm opacity-80">You rejected the file from {incomingReq.peer.name}.</p></div>
                </div>
              ) : incomingReq.state === 'completed' ? (
                <div className="flex items-center justify-between">
                  <div className={`flex items-center gap-4 ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                    <CheckCircle className="w-8 h-8" />
                    <div><p className="font-bold">File Received!</p><p className="text-sm opacity-80">Saved to your downloads.</p></div>
                  </div>
                  <button onClick={() => setIncomingReq(null)} className={`px-4 py-2 rounded-xl font-bold text-sm transition-colors ${darkMode ? 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30' : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'}`}>Close</button>
                </div>
              ) : (
                <>
                  <div className="flex justify-between items-end mb-3">
                    <div>
                      <h3 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>Receiving...</h3>
                      <p className={`text-sm font-medium ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>From {incomingReq.peer.name}</p>
                    </div>
                    <span className={`text-2xl font-black ${darkMode ? 'text-indigo-500' : 'text-blue-600'}`}>{Math.round(receiverProgress)}%</span>
                  </div>
                  <div className={`h-3 w-full rounded-full overflow-hidden shadow-inner ${darkMode ? 'bg-slate-800' : 'bg-slate-100'}`}>
                    <motion.div className={`h-full rounded-full ${darkMode ? 'bg-indigo-500' : 'bg-blue-500'}`} initial={{ width: 0 }} animate={{ width: `${receiverProgress}%` }} transition={{ ease: 'linear', duration: 0.3 }} />
                  </div>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Action Panel (Sender UI) */}
      <AnimatePresence>
        {(selectedPeer || selectedFiles.length > 0) && !incomingReq && (
          <motion.div
            initial={{ y: 100, opacity: 0, scale: 0.95 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 100, opacity: 0, scale: 0.95 }}
            transition={{ type: 'spring', damping: 25, stiffness: 250 }}
            className="absolute bottom-8 left-1/2 -translate-x-1/2 w-full max-w-lg z-40 px-4"
          >
            <div className={`backdrop-blur-2xl rounded-[2rem] shadow-2xl overflow-hidden flex flex-col border transition-colors duration-500 ${darkMode ? 'bg-slate-900/80 border-slate-700/50 shadow-black/50' : 'bg-white/80 border-white shadow-slate-900/10'}`}>

              {/* Header */}
              <div className={`flex items-center justify-between p-5 border-b transition-colors duration-500 ${darkMode ? 'border-slate-800/50 bg-slate-900/50' : 'border-slate-200/50 bg-white/50'}`}>
                {selectedPeer ? (
                  <div className="flex items-center gap-3">
                    <img src={selectedPeer.avatar} alt="Peer" className={`w-10 h-10 rounded-full border object-cover ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-100 border-slate-200'}`} />
                    <div>
                      <p className={`text-[11px] font-bold uppercase tracking-wider ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Sending to</p>
                      <div className="flex items-center gap-1.5">
                        <p className={`text-base font-bold leading-tight ${darkMode ? 'text-white' : 'text-slate-900'}`}>{selectedPeer.name}</p>
                        <div className={darkMode ? 'text-slate-500' : 'text-slate-400'}>{getDeviceIcon(selectedPeer.device, 'w-3 h-3')}</div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${darkMode ? 'bg-indigo-500/20 text-indigo-400' : 'bg-blue-100 text-blue-600'}`}>
                      <Upload className="w-5 h-5" />
                    </div>
                    <div>
                      <p className={`text-[11px] font-bold uppercase tracking-wider ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>File Selected</p>
                      <p className={`text-base font-bold leading-tight ${darkMode ? 'text-white' : 'text-slate-900'}`}>Select a peer to send</p>
                    </div>
                  </div>
                )}
                {transferState === 'idle' && (
                  <button onClick={resetSelection} className={`p-2 rounded-full transition-colors ${darkMode ? 'text-slate-400 hover:text-white hover:bg-slate-800/50' : 'text-slate-400 hover:text-slate-700 hover:bg-slate-100/50'}`}>
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>

              {/* Content */}
              <div className="p-6">
                <AnimatePresence mode="wait">

                  {/* State 1: Select File/Folder */}
                  {transferState === 'idle' && selectedFiles.length === 0 && selectedPeer && (
                    <motion.div key="select-file" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="flex gap-4">
                      <button onClick={() => fileInputRef.current?.click()} className={`flex-1 flex flex-col items-center justify-center gap-3 p-6 rounded-3xl border transition-all group ${darkMode ? 'border-slate-800 bg-slate-900/50 hover:bg-slate-800 hover:border-indigo-500/50 hover:shadow-lg hover:shadow-indigo-500/10 text-slate-300 hover:text-indigo-400' : 'border-slate-200 bg-white/50 hover:bg-white hover:border-blue-400 hover:shadow-lg hover:shadow-blue-500/5 text-slate-600 hover:text-blue-600'}`}>
                        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-colors ${darkMode ? 'bg-slate-800 group-hover:bg-indigo-500/20' : 'bg-slate-100 group-hover:bg-blue-50'}`}><Upload className="w-6 h-6" /></div>
                        <span className="font-bold text-sm">Upload File</span>
                      </button>
                      <button onClick={() => folderInputRef.current?.click()} className={`flex-1 flex flex-col items-center justify-center gap-3 p-6 rounded-3xl border transition-all group ${darkMode ? 'border-slate-800 bg-slate-900/50 hover:bg-slate-800 hover:border-indigo-500/50 hover:shadow-lg hover:shadow-indigo-500/10 text-slate-300 hover:text-indigo-400' : 'border-slate-200 bg-white/50 hover:bg-white hover:border-blue-400 hover:shadow-lg hover:shadow-blue-500/5 text-slate-600 hover:text-blue-600'}`}>
                        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-colors ${darkMode ? 'bg-slate-800 group-hover:bg-indigo-500/20' : 'bg-slate-100 group-hover:bg-blue-50'}`}><FolderUp className="w-6 h-6" /></div>
                        <span className="font-bold text-sm">Upload Folder</span>
                      </button>
                      <input type="file" className="hidden" ref={fileInputRef} onChange={handleFileSelect} multiple />
                      <input type="file" className="hidden" ref={folderInputRef} onChange={handleFileSelect}
                        // @ts-ignore
                        webkitdirectory="true" directory="true" />
                    </motion.div>
                  )}

                  {/* State 2: Ready to Send */}
                  {transferState === 'idle' && selectedFiles.length > 0 && (
                    <motion.div key="ready-send" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="flex flex-col">
                      <div className={`border rounded-2xl p-4 shadow-sm flex items-center gap-4 mb-6 transition-colors duration-500 ${darkMode ? 'bg-slate-900/80 border-slate-800' : 'bg-white border-slate-200/60'}`}>
                        {filePreview ? (
                          <div className={`w-16 h-16 rounded-xl overflow-hidden flex-none border ${darkMode ? 'border-slate-700 bg-slate-800' : 'border-slate-200 bg-slate-50'}`}>
                            <img src={filePreview} alt="Preview" className="w-full h-full object-cover" />
                          </div>
                        ) : (
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-none ${darkMode ? 'bg-indigo-500/20 text-indigo-400' : 'bg-blue-50 text-blue-600'}`}>
                            {getFileIcon(selectedFiles[0]?.type ?? '')}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <h3 className={`text-sm font-bold truncate ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                            {selectedFiles.length > 1 ? `${selectedFiles.length} files selected` : selectedFiles[0]?.name}
                          </h3>
                          <p className={`text-xs font-medium mt-0.5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            {(selectedFiles.reduce((acc, file) => acc + file.size, 0) / (1024 * 1024)).toFixed(2)} MB total
                          </p>
                        </div>
                        <button onClick={() => setSelectedFiles([])} className={`p-2 rounded-full transition-colors flex-none ${darkMode ? 'text-slate-400 hover:text-white hover:bg-slate-800' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'}`}><X className="w-5 h-5" /></button>
                      </div>
                      {selectedPeer ? (
                        <button onClick={handleSend} className={`w-full py-4 rounded-2xl font-bold text-white shadow-lg transition-all flex items-center justify-center gap-2 group ${darkMode ? 'bg-indigo-600 hover:bg-indigo-500 shadow-indigo-500/20' : 'bg-blue-600 hover:bg-blue-700 shadow-blue-600/20'}`}>
                          Send to {selectedPeer.name} <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                        </button>
                      ) : (
                        <div className={`w-full py-4 rounded-2xl font-bold text-center border border-dashed ${darkMode ? 'text-slate-500 bg-slate-900/50 border-slate-700' : 'text-slate-500 bg-slate-50 border-slate-200'}`}>
                          Select a peer on the radar to send
                        </div>
                      )}
                    </motion.div>
                  )}

                  {/* State 3: Awaiting Accept */}
                  {transferState === 'awaiting_accept' && selectedPeer && (
                    <motion.div key="awaiting" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} className="flex flex-col items-center py-6 text-center">
                      <div className={`w-16 h-16 rounded-full border-4 shadow-sm flex items-center justify-center mb-4 relative ${darkMode ? 'bg-slate-900 border-slate-800' : 'bg-slate-100 border-white'}`}>
                        <div className={`absolute inset-0 rounded-full border-2 border-t-transparent animate-spin ${darkMode ? 'border-indigo-500' : 'border-blue-500'}`}></div>
                        <Clock className={`w-6 h-6 ${darkMode ? 'text-slate-400' : 'text-slate-400'}`} />
                      </div>
                      <h3 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>Waiting for {selectedPeer.name}...</h3>
                      <p className={`text-sm mb-6 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>They need to accept the transfer.</p>
                      <button onClick={cancelTransfer} className={`py-2 px-6 rounded-xl font-bold transition-colors ${darkMode ? 'text-slate-300 bg-slate-800 hover:bg-slate-700' : 'text-slate-600 bg-slate-100 hover:bg-slate-200'}`}>Cancel Request</button>
                    </motion.div>
                  )}

                  {/* State 4: Rejected / Cancelled */}
                  {(transferState === 'rejected' || transferState === 'cancelled') && selectedPeer && (
                    <motion.div key="failed" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} className="flex flex-col items-center py-4 text-center">
                      <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${darkMode ? 'bg-rose-500/20 text-rose-400' : 'bg-rose-50 text-rose-500'}`}>
                        {transferState === 'rejected' ? <XCircle className="w-8 h-8" /> : <AlertCircle className="w-8 h-8" />}
                      </div>
                      <h3 className={`text-lg font-bold mb-1 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                        {transferState === 'rejected' ? 'Transfer Declined' : 'Transfer Cancelled'}
                      </h3>
                      <p className={`text-sm mb-6 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        {transferState === 'rejected'
                          ? `${selectedPeer.name} declined your file.`
                          : (transferError || 'You cancelled the transfer.')}
                      </p>
                      <button onClick={resetSelection} className={`py-2 px-6 rounded-xl font-bold transition-colors ${darkMode ? 'text-slate-300 bg-slate-800 hover:bg-slate-700' : 'text-slate-600 bg-slate-100 hover:bg-slate-200'}`}>Try Again</button>
                    </motion.div>
                  )}

                  {/* State 5: Transferring / Completed */}
                  {(transferState === 'transferring' || transferState === 'completed') && selectedPeer && (
                    <motion.div key="transferring" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="flex flex-col">
                      <div className="flex justify-between items-end mb-3">
                        <div>
                          <h3 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>{transferState === 'completed' ? 'Transfer Complete' : 'Sending...'}</h3>
                          <p className={`text-sm font-medium truncate max-w-[250px] ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            {selectedFiles.length > 1 ? `${selectedFiles.length} files` : selectedFiles[0]?.name}
                          </p>
                        </div>
                        <span className={`text-3xl font-black ${transferState === 'completed' ? 'text-emerald-500' : (darkMode ? 'text-indigo-500' : 'text-blue-600')}`}>{Math.round(progress)}%</span>
                      </div>
                      <div className={`h-3 w-full rounded-full overflow-hidden mb-6 shadow-inner ${darkMode ? 'bg-slate-800' : 'bg-slate-100'}`}>
                        <motion.div className={`h-full rounded-full ${transferState === 'completed' ? 'bg-emerald-500' : (darkMode ? 'bg-indigo-500' : 'bg-blue-500')}`} initial={{ width: 0 }} animate={{ width: `${progress}%` }} transition={{ ease: 'linear', duration: 0.3 }} />
                      </div>
                      {transferState === 'transferring' ? (
                        <div className="flex flex-col gap-4">
                          <div className="grid grid-cols-2 gap-3">
                            <div className={`border rounded-2xl p-3 shadow-sm flex flex-col ${darkMode ? 'bg-slate-900/50 border-slate-800' : 'bg-white border-slate-200/60'}`}>
                              <span className={`uppercase text-[10px] font-bold tracking-wider mb-1 flex items-center gap-1 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}><Zap className="w-3 h-3" /> Speed</span>
                              <span className={`text-lg font-bold ${darkMode ? 'text-slate-200' : 'text-slate-900'}`}>{transferRate || '—'}</span>
                            </div>
                            <div className={`border rounded-2xl p-3 shadow-sm flex flex-col ${darkMode ? 'bg-slate-900/50 border-slate-800' : 'bg-white border-slate-200/60'}`}>
                              <span className={`uppercase text-[10px] font-bold tracking-wider mb-1 flex items-center gap-1 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}><Clock className="w-3 h-3" /> Time Left</span>
                              <span className={`text-lg font-bold ${darkMode ? 'text-slate-200' : 'text-slate-900'}`}>{timeRemaining || '—'}</span>
                            </div>
                          </div>
                          <button onClick={cancelTransfer} className={`w-full py-3 rounded-xl font-bold transition-colors ${darkMode ? 'text-rose-400 bg-rose-500/10 hover:bg-rose-500/20' : 'text-rose-600 bg-rose-50 hover:bg-rose-100'}`}>Cancel Transfer</button>
                        </div>
                      ) : (
                        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className={`flex items-center justify-between border rounded-2xl p-4 ${darkMode ? 'bg-emerald-500/10 border-emerald-500/20' : 'bg-emerald-50 border-emerald-200/60'}`}>
                          <div className={`flex items-center gap-3 ${darkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>
                            <CheckCircle className="w-8 h-8" />
                            <div>
                              <p className="font-bold text-sm">Successfully Delivered</p>
                              <p className="text-xs font-medium opacity-80">Saved to receiver's device.</p>
                            </div>
                          </div>
                          <button onClick={resetSelection} className={`py-2 px-4 rounded-xl font-bold text-sm transition-colors ${darkMode ? 'text-emerald-400 bg-emerald-500/20 hover:bg-emerald-500/30' : 'text-emerald-700 bg-emerald-200/50 hover:bg-emerald-200'}`}>Done</button>
                        </motion.div>
                      )}
                    </motion.div>
                  )}

                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* History Drawer */}
      <AnimatePresence>
        {isHistoryOpen && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setIsHistoryOpen(false)} className="absolute inset-0 bg-[#020205]/40 backdrop-blur-md z-50" />
            <motion.div
              initial={{ x: '100%', opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: '100%', opacity: 0 }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className={`absolute top-0 right-0 bottom-0 w-full max-w-md shadow-2xl z-50 flex flex-col border-l transition-colors duration-500 overflow-hidden ${darkMode ? 'bg-[#0a0a10]/95 backdrop-blur-3xl border-slate-800/50' : 'bg-white/95 backdrop-blur-3xl border-slate-200/50'}`}
            >
              <div className={`p-8 flex items-center justify-between transition-colors duration-500 ${darkMode ? 'bg-gradient-to-b from-slate-900/50 to-transparent' : 'bg-gradient-to-b from-slate-50/50 to-transparent'}`}>
                <h2 className={`text-2xl font-bold flex items-center gap-3 transition-colors duration-500 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                  <div className={`p-2.5 rounded-2xl ${darkMode ? 'bg-indigo-500/20 text-indigo-400 shadow-inner shadow-indigo-500/20' : 'bg-blue-50 text-blue-600 shadow-inner shadow-blue-500/10'}`}>
                    <History className="w-6 h-6" />
                  </div>
                  Transfer History
                </h2>
                <button onClick={() => setIsHistoryOpen(false)} className={`p-2.5 rounded-full transition-all duration-300 ${darkMode ? 'hover:bg-slate-800 text-slate-400 hover:text-white' : 'hover:bg-slate-100 text-slate-500 hover:text-slate-900'}`}>
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto px-8 pb-8 space-y-4">
                {history.length === 0 ? (
                  <div className="h-full flex flex-col items-center justify-center text-slate-400">
                    <div className={`w-32 h-32 rounded-full flex items-center justify-center mb-6 transition-colors ${darkMode ? 'bg-slate-800/30 border border-slate-700/50' : 'bg-slate-50 border border-slate-100'}`}>
                      <Archive className="w-12 h-12 opacity-30" />
                    </div>
                    <p className={`text-xl font-bold ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>No transfers yet</p>
                    <p className="text-sm opacity-60 mt-2">Your transfer history will appear here.</p>
                  </div>
                ) : (
                  history.map(record => (
                    <div key={record.id} className={`p-5 rounded-3xl border flex items-center gap-5 transition-all duration-300 hover:scale-[1.02] cursor-default ${darkMode ? 'bg-slate-800/20 border-slate-700/50 hover:bg-slate-800/40 hover:border-slate-600/50' : 'bg-white border-slate-200/60 hover:shadow-lg hover:shadow-slate-200/50 hover:border-slate-300/60'}`}>
                      <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-inner ${record.type === 'sent' ? (darkMode ? 'bg-indigo-500/20 text-indigo-400' : 'bg-blue-50 text-blue-600') : (darkMode ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-50 text-emerald-600')}`}>
                        {record.type === 'sent' ? <Upload className="w-6 h-6" /> : <Download className="w-6 h-6" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-2">
                          <p className={`text-base font-bold truncate transition-colors duration-500 ${darkMode ? 'text-slate-200' : 'text-slate-800'}`}>{record.fileName}</p>
                          <span className={`text-[10px] font-bold uppercase px-3 py-1.5 rounded-full tracking-wider ${record.status === 'completed' ? (darkMode ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-emerald-50 text-emerald-700 border border-emerald-200') : record.status === 'failed' ? (darkMode ? 'bg-rose-500/10 text-rose-400 border border-rose-500/20' : 'bg-rose-50 text-rose-700 border border-rose-200') : (darkMode ? 'bg-slate-800 text-slate-300 border border-slate-700' : 'bg-slate-100 text-slate-700 border border-slate-200')}`}>
                            {record.status}
                          </span>
                        </div>
                        <div className={`flex items-center gap-3 text-sm font-medium transition-colors duration-500 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                          <span className="flex items-center gap-1.5">
                            <ArrowRight className={`w-3 h-3 ${record.type === 'received' ? 'rotate-180' : ''}`} />
                            <span className={darkMode ? 'text-slate-300' : 'text-slate-700'}>{record.peer.name}</span>
                          </span>
                          <span className="opacity-30">•</span>
                          <span>{record.fileSize.toFixed(1)} MB</span>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

    </div>
  );
}
